"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import {
  FaPlay,
  FaPause,
  FaStop,
  FaVolumeUp,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import hljs from "highlight.js";
import "highlight.js/styles/github-dark.css"; // Dark mode syntax highlighting
import { Article } from "@/types";

const ArticleContent = ({ article }: { article: Article }) => {
  const [fontSize, setFontSize] = useState("text-lg");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Highlight.js syntax highlighting
  useEffect(() => {
    document.querySelectorAll("pre code").forEach((block) => {
      hljs.highlightElement(block as HTMLElement);
    });
  }, [article.content]);

  // Adjust font size
  const increaseFontSize = () => {
    setFontSize((prev) => (prev === "text-lg" ? "text-xl" : "text-2xl"));
  };
  const decreaseFontSize = () => {
    setFontSize((prev) => (prev === "text-2xl" ? "text-xl" : "text-lg"));
  };

  // Extract plain text from HTML
  const extractPlainText = (htmlContent: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || "";
  };

  // Start / Pause / Resume / Stop text-to-speech
  const toggleAudioPlayback = () => {
    if ("speechSynthesis" in window) {
      if (isSpeaking && isPaused) {
        window.speechSynthesis.resume();
        setIsPaused(false);
      } else if (isSpeaking) {
        window.speechSynthesis.pause();
        setIsPaused(true);
      } else {
        startReading();
      }
    } else {
      alert("Votre navigateur ne prend pas en charge la synthèse vocale.");
    }
  };

  const stopAudioPlayback = () => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setIsPaused(false);
      removeHighlights();
    }
  };

  const removeHighlights = () => {
    if (contentRef.current) {
      contentRef.current
        .querySelectorAll(".highlighted")
        .forEach((el) => el.classList.remove("highlighted"));
    }
  };

  const startReading = () => {
    if ("speechSynthesis" in window && contentRef.current) {
      const plainText = extractPlainText(article.content);
      const utterance = new SpeechSynthesisUtterance(plainText);
      utterance.lang = "fr-FR";
      utterance.rate = 0.9;
      utterance.pitch = 1;

      utterance.onboundary = (event) => {
        const { charIndex } = event;
        const textNodes = Array.from(
          contentRef.current!.querySelectorAll("p, li, h1, h2, h3, h4, h5, h6"),
        ).map((node) => node.textContent || "");
        const matchingNodeIndex = textNodes.findIndex((text) =>
          text.includes(plainText.slice(charIndex, charIndex + 10).trim()),
        );

        removeHighlights();
        if (matchingNodeIndex !== -1) {
          const elements = contentRef.current!.children;
          elements[matchingNodeIndex]?.classList.add("highlighted");
        }
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setIsPaused(false);
        removeHighlights();
      };

      window.speechSynthesis.cancel();
      setIsSpeaking(true);
      setIsPaused(false);
      window.speechSynthesis.speak(utterance);
    }
  };

  const wordCount = article.content.split(/\s+/).length;
  const readingTime = Math.ceil(wordCount / 200);

  return (
    <div className="font-sans text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      {/* Titre */}
      <h1 className="mb-2 hyphens-auto whitespace-normal break-words text-justify text-xl font-semibold text-gray-900 dark:text-gray-100">
        {article.title}
      </h1>

      {/* Métadonnées */}
      <p className="mt-2 text-sm text-blue-600 dark:text-blue-400">
        {article.categories?.map((a) => a.name).join(", ")} •{" "}
        {article.published_date} •{" "}
        {article.authors?.map((a) => a.name).join(", ")}
      </p>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        ⏳ {readingTime} min de lecture
      </p>

      {/* Toolbar */}
      <div className="mt-4 flex items-center space-x-4">
        {/* Font Size Controls */}
        <button
          onClick={decreaseFontSize}
          className="flex items-center space-x-2 rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
        >
          <span>A</span>
          <FaMinus />
        </button>
        <button
          onClick={increaseFontSize}
          className="flex items-center space-x-2 rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
        >
          <span>A</span>
          <FaPlus />
        </button>

        {/* Audio Playback Button */}
        <button
          onClick={toggleAudioPlayback}
          className={`flex items-center space-x-2 rounded px-4 py-2 font-semibold text-white ${
            isSpeaking && isPaused
              ? "bg-yellow-500 hover:bg-yellow-600"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {isSpeaking && isPaused ? (
            <FaPlay />
          ) : isSpeaking ? (
            <FaPause />
          ) : (
            <FaVolumeUp />
          )}
          <span>
            {isSpeaking && isPaused
              ? "Reprendre"
              : isSpeaking
                ? "Pause"
                : "Écouter"}
          </span>
        </button>

        {/* Stop Button */}
        {isSpeaking && (
          <button
            onClick={stopAudioPlayback}
            className="flex items-center space-x-2 rounded bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700"
          >
            <FaStop />
            <span>Arrêter</span>
          </button>
        )}
      </div>

      {/* Image en vedette */}
      {article.cover_url && (
        <div className="my-6 flex flex-col items-center">
          <Image
            src={article.cover_url}
            alt={article.title}
            width={600}
            height={375}
            priority
            fetchPriority="high"
            className="w-full shadow-lg sm:rounded-lg"
          />
          <p className="mt-2 text-center text-sm italic text-gray-500 dark:text-gray-400">
            {article.caption || "Image source: Les Concernés"}
          </p>
        </div>
      )}

      {/* Contenu de l'article */}
      <div
        ref={contentRef}
        className={`prose dark:prose-invert ${fontSize} mt-6 space-y-6 hyphens-auto whitespace-normal break-words text-justify leading-relaxed`}
        dangerouslySetInnerHTML={{ __html: article.content }}
      />
    </div>
  );
};

export default ArticleContent;
